import { getTransactions } from 'apis';
import { Intro, DataTable, Pill, Text, PopOverMenu } from 'components/commons';
import { useApi, useFilter, useModal, useMount } from 'hooks';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { capitalize, dateTimeFormat, formatAmount } from 'utils';
import { transactionColumns } from './transaction-columns';
import { transactionFDColumns } from './transaction-columns-fd';
import { transactionSCColumns } from './transaction-columns-sc';
import TransactionFilter from './transaction-filter';
import { transactionFilterState } from './transaction-filter.state';
import styles from './transaction.module.scss';
import locale from 'localization';
import { SuccessModal } from 'components/modals';
import TransactionForm from './transaction-form';
import TransactionStatusModal from './transaction-status-modal';
import { prettifyProduct } from 'utils/pretty.utils';
import ProductCmms from 'enums/products-cmms';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { getProducts } from 'apis/product.api';
import { getCustomerAccounts } from 'apis/customer.api';
import PrintPassbookModal from './print-passbook-modal';
import { DateTime } from 'enums';
import * as moment from 'moment';
import { generateReport } from 'apis/generate-report.api';
import { UserContext } from 'contexts';

const TransactionModule = ({ product }) => {
  const user = useContext(UserContext);

  // const [productId, setProductId] = useState(null);
  const [customer, setCustomer] = useState(null);

  const location = useLocation();
  const history = useHistory();

  const queryParams = new URLSearchParams(location.search);
  const searchKey = queryParams.get('customerAccountNumber');

  const statusModal = useModal();
  const successModal = useModal();
  const printPassbookModal = useModal();

  let columns;

  switch (product) {
    case ProductCmms.FixedDeposit: {
      columns = transactionFDColumns;
      break;
    }
    case ProductCmms.SharedCapital: {
      columns = transactionSCColumns;
      break;
    }
    default: {
      columns = transactionColumns;
      break;
    }
  }

  const { modifyFilter, modifyFilters, filterState, requestState, submitFilter } = useFilter(transactionFilterState());
  const transactionFormModal = useModal();

  const {
    request: getTransactionRequest,
    loading: fetchingTransactions,
    result: getTransactionsResult = { transactions: [], count: 0 }
  } = useApi({
    api: getTransactions,
    params: {
      product: prettifyProduct(product),
      customerAccountNumber: searchKey
    },
    pageError: true
  });

  const {
    request: getProductRequest,
    loading: fetchingProduct,
    result: getProductResult = { products: [] }
  } = useApi({
    api: getProducts,
    params: {
      searchKey: prettifyProduct(product)
    },
    pageError: true
  });

  const {
    request: getCustomerAccountRequest,
    loading: fetchingCustomerAccount,
    result: getCustomerAccountResult = { customerAccounts: [] }
  } = useApi({
    api: getCustomerAccounts,
    params: {
      customerAccountNumber: searchKey
    },
    pageError: true
  });

  useEffect(() => {
    if (getProductResult.products?.length > 0) {
      // setProductId(getProductResult.products[0].productId);
    }
  }, [getProductResult]);

  useEffect(() => {
    if (getCustomerAccountResult.customerAccounts?.length > 0) {
      const customerAccount = getCustomerAccountResult.customerAccounts[0];

      setCustomer({
        customerId: customerAccount.customerId,
        cifNumber: customerAccount?.customer?.cifNumber,
        status: customerAccount?.status
      });
    }
  }, [getCustomerAccountResult]);

  useMount(() => {
    getCustomerAccountRequest();
    getProductRequest();
    fetchTransactions({ ...requestState, product: prettifyProduct(product), customerAccountNumber: searchKey });
  });

  const fetchTransactions = useCallback(
    (rs) => {
      if (!rs.status) {
        if (user?.role === 'maker') {
          rs.status = 'success';
        }
      }

      getTransactionRequest(submitFilter({ ...rs }));
    },
    [getTransactionRequest, submitFilter, user?.role]
  );

  const changeStatusCb = useCallback(
    (transactionId, customerAccountNumber, type, amount, productName, status, requestAction) => {
      statusModal.show({ transactionId, customerAccountNumber, type, amount, productName, status, requestAction });
    },
    [statusModal]
  );

  const preparedTransactionsData = useMemo(() => {
    const { transactions } = getTransactionsResult;
    if (transactions.length > 0) {
      const preparedData = transactions.map((d, index) => {
        const map = new Map();
        map.set('rowNo', index + 1);
        map.set(
          'type',
          <div>
            <Pill cement={d.type === 'Withdrawal'} cheddar={d.type === 'Dividend'} sky={d.type === 'Deposit'}>
              {d.type}
            </Pill>
          </div>
        );
        map.set('transactionId', d.transactionId);
        map.set(
          'customerAccountNumber',
          <div>
            <Text>{d.customerAccountNumber}</Text>
            <Text className={styles.subBusinessId}>{d.cifNumber}</Text>
          </div>
        );
        map.set('amount', formatAmount(d?.amount || 0));
        map.set('product', d.productName);
        map.set('balance', formatAmount(d?.balance || 0));
        map.set('newBalance', formatAmount(d?.newBalance || 0));
        map.set('createdAt', dateTimeFormat(d.createdAt));
        map.set('transactedBy', d.transactedBy);
        map.set('tranCode', d?.tranCode);
        map.set(
          'status',
          <div>
            <Pill grass={d.status === 'success'} deepRed={d.status === 'voided'} cheddar={d.status === 'pending'}>
              {capitalize(d.status)}
            </Pill>
            {d.status === 'voided' && <Text className={styles.subBusinessId}>{dateTimeFormat(d.voidedAt)}</Text>}
          </div>
        );

        const options = [];

        if (['super-admin', 'admin'].includes(user.role)) {
          // options.push({
          //   content: 'Void Transaction',
          //   onClick: () => {
          //     changeStatusCb(
          //       d.transactionId,
          //       d.customerAccountNumber,
          //       d.type,
          //       d.amount,
          //       d.productName,
          //       'voided',
          //       d.requestAction
          //     );
          //   }
          // });
        }

        if (['approver', 'super-admin'].includes(user.role) && d.status === 'pending') {
          options.push({
            content: 'Approve',
            onClick: () => {
              changeStatusCb(
                d.transactionId,
                d.customerAccountNumber,
                d.type,
                d.amount,
                d.productName,
                'approve',
                d.requestAction
              );
            }
          });

          options.push({
            content: 'Reject',
            onClick: () => {
              changeStatusCb(
                d.transactionId,
                d.customerAccountNumber,
                d.type,
                d.amount,
                d.productName,
                'reject',
                d.requestAction
              );
            }
          });
        }

        if (['success', 'pending'].includes(d.status)) {
          map.set('options', options && options.length > 0 && <PopOverMenu options={options} />);
        }

        return map;
      });
      return preparedData;
    }
    return [];
  }, [changeStatusCb, getTransactionsResult, user.role]);

  const onTypeChangeCb = useCallback(
    (type) => {
      const { requestState } = modifyFilters({ type, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onSearchCb = useCallback(
    (searchKeyValue) => {
      const { requestState } = modifyFilters({
        searchKey: searchKeyValue,
        page: 1,
        product: prettifyProduct(product),
        customerAccountNumber: searchKey
      });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters, product, searchKey]
  );

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const onDateRangeCb = useCallback(
    (value) => {
      const { startDate, endDate } = value;
      const { requestState } = modifyFilters({ startDate, endDate, page: 1 });
      fetchTransactions(requestState);
    },
    [modifyFilters, fetchTransactions]
  );

  const onProductChangeCb = useCallback(
    (product) => {
      const { requestState } = modifyFilters({ product, productId: product.productId, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const { request: generateReportRequest, loading: fetchingReport } = useApi({
    api: generateReport,
    pageError: true,
    params: {
      product: prettifyProduct(product),
      customerAccountNumber: searchKey
    }
  });

  const onPrintPassbookCb = () => {
    printPassbookModal.show();
  };

  const onDownloadFileCb = async () => {
    const { requestState } = modifyFilters({ reportType: 'transaction', product: prettifyProduct(product) });

    delete requestState.perPage;
    delete requestState.page;

    const generateResult = await generateReportRequest(requestState);
    if (generateResult?.downloadUrl) {
      window.open(generateResult?.downloadUrl, '_blank');
    }
  };

  const onStatusChangeCb = useCallback(
    (status) => {
      const { requestState } = modifyFilters({ status, page: 1 });
      fetchTransactions(requestState);
    },
    [fetchTransactions, modifyFilters]
  );

  const handlePrintRange = ({ from, to, start }) => {
    const userId = localStorage.getItem('userId');
    // const transactions = [...getTransactionsResult.transactions].sort((a, b) => a.transactionId - b.transactionId);
    const transactions = getTransactionsResult.transactions;

    const startIdx = from - 1;
    const endIdx = to;

    let selected = transactions.slice(startIdx, endIdx);

    // selected = selected.sort((a, b) => a.transactionId - b.transactionId);

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const lineOffset = Number(start || 1) - 1;
    const lineBreaks = Array.from({ length: lineOffset })
      .map(() => '<br />')
      .join('');

    let defaultLine = `<br/><br/><br/><br/><br/>`;
    if (start === 18) {
      defaultLine = '<br />';
    } else if (start > 18) {
      defaultLine = ``;
    }
    const html = `
    ${defaultLine}
    ${lineBreaks}
    <html>
    <head>
       <style>
       @page {
          margin-top: 0px;
          margin-bottom: 0px;
          margin-left: 20px;
          margin-right: 20px;
        }
        body {
          padding: 0px;
          margin: 0px;
          font-family: arial-narrow, sans-serif;
        }
        table {
          width: 60%;
          border-collapse: collapse;
          table-layout: fixed;
        }
        td {
          font-size: 10px;
          word-wrap: break-word;
          box-sizing: border-box;
        }
        td:nth-child(1) { width: 14%; text-align: left; }
        td:nth-child(2) { width: 14%; text-align: left; }
        td:nth-child(3) { width: 14%; text-align: right; }
        td:nth-child(4) { width: 14%; text-align: right; }
        td:nth-child(5) { width: 14%; text-align: right; }
        td:nth-child(6) { width: 14%; text-align: center; }
        td:nth-child(7) { width: 14%; text-align: center; }
      </style>
    </head>
    <body>
      <table>
        <tbody>
          ${selected
            .map(
              (tx, idx) => `
            <tr>
              <td>${start + idx}&nbsp;&nbsp;${moment(tx.createdAt).format(DateTime.U)}</td>
              <td>${tx.orNumber || ''}</td>
              <td>${['Deposit', 'Dividend'].includes(tx.type) ? formatAmount(tx.amount, '') : ''}</td>
              <td>${tx.type === 'Withdrawal' ? formatAmount(tx.amount, '') : ''}</td>
              <td>${formatAmount(tx.newBalance, '')}</td>
              <td>${userId}</td>
              <td>${tx.tranCode ?? ''}</td>
            </tr>
          `
            )
            .join('')}
        </tbody>
      </table>
      <script>
        window.onload = function () {
          setTimeout(() => {
            window.print();
            window.onafterprint = () => window.close();
          }, 500);
        }
      </script>
    </body>
    </html>
  `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div>
      <SuccessModal {...successModal} />
      <TransactionStatusModal refetchTransactions={() => fetchTransactions(requestState)} {...statusModal} />
      <PrintPassbookModal onConfirm={handlePrintRange} {...printPassbookModal} />
      <TransactionForm
        {...transactionFormModal}
        refetchTransactions={() => fetchTransactions(requestState)}
        refreshPage={() => {
          fetchTransactions(requestState);
        }}
        product={product}
      />

      <Intro
        title={searchKey ? `${prettifyProduct(product)} - ${searchKey}` : prettifyProduct(product)}
        subtitle={locale.manageYourTransactions}
        // actionText={searchKey && 'Add Transaction'}
        actionSecondaryText={searchKey && 'Back to Accounts'}
        actionSecondaryOnClick={() => {
          history.goBack();
        }}
        actionDisabled={customer?.status !== 'active'}
        // actionOnClick={() => {
        //   transactionFormModal.show({
        //     title: 'Add Transaction',
        //     actionText: 'Add Transaction',
        //     create: true,
        //     customerAccountNumber: searchKey,
        //     initialState: {
        //       customerId: {
        //         label: customer?.cifNumber,
        //         value: customer?.customerId
        //       },
        //       productId: {
        //         label: prettifyProduct(product),
        //         value: productId
        //       },
        //       amount: {
        //         value: '',
        //         disabled: false
        //       },
        //       type: {
        //         value: ''
        //       }
        //     }
        //   });
        // }}
      />

      <div className={styles.filters}>
        <div>
          <TransactionFilter
            role={user?.role}
            filterState={filterState}
            onTypeChange={onTypeChangeCb}
            onSearchChange={modifyFilter}
            onSearch={onSearchCb}
            onDateRangeChange={onDateRangeCb}
            onPrintPassbook={onPrintPassbookCb}
            onProductChange={onProductChangeCb}
            onDownloadFile={onDownloadFileCb}
            onStatusChange={onStatusChangeCb}
            fetchingReport={fetchingReport}
            customer={searchKey}
            product={product}
          />
        </div>
      </div>
      <div className={styles.table}>
        <DataTable
          loading={fetchingTransactions || fetchingProduct || fetchingCustomerAccount}
          page={filterState.page}
          pageSize={filterState.perPage}
          columns={columns}
          data={preparedTransactionsData}
          dataCount={getTransactionsResult.count}
          onChangePage={onChangePageCb}
          onChangePageSize={onChangePageSizeCb}
        />
      </div>
    </div>
  );
};

export default TransactionModule;
